import './DodFactSetPreview.scss';
import { ReactNode, useEffect, useState, useMemo } from 'react';
import classnames from 'classnames';
import { ErrorBoundary } from 'react-error-boundary';
import { ByzzerChangeEventHandler } from '@byzzer/ui-components';

import { DodFilterPreviewGroup, DodSelectionPreviewNode } from '@/components/DodConfigEditor/common';
import { DodPanel } from '@/components/DodConfigEditor/common/DodPanel';
import { openCreateDodPresetModal } from '@/components/DodConfigEditor/common/DodSavedSelection/DodSaveSelectionModal';
import { useDodWizard } from '@/components/DodConfigEditor/DodRunConfigWizard/DodWizardContext';
import {
    DodFactSuffixes,
    getConditionNamePreservingCustom,
    factSetToCoreDisplayNames,
    factSetToSharedDisplayNames,
} from '@/components/DodConfigEditor/common/utils';
import { useApp } from '@/contexts/UserContext';
import { useDodService } from '@/services/dodPresets.service';
import { DodFactSet } from '@/types/DodRun';
import { useUniversalSelection } from '@/services/universalSelection.service';
import { DOMPurify } from '@/utils';
import { confirm } from '@/components/form/ByzzerModal';

const baseClassName = 'dod-fact-set-preview';

export type DodFactSetPreviewProps = {
    className?: string;
    children?: ReactNode;
    value: DodFactSet[];
    title?: ReactNode;
    emptyStateContent?: ReactNode;
    onChange?: ByzzerChangeEventHandler<DodFactSet[]>;
    name?: string
};

const savedSelectionDescription = <>
    <p>This will save the full set of selections currently in your selection panel so that you can easily reuse them in
        future extracts.</p>
    <p>Your saved selections will be available in the "My Saved Facts" folder on the left.</p>
</>;

export function DodFactSetPreview({
    className,
    value,
    onChange,
    title,
    name,
    emptyStateContent,
    ...props
}: DodFactSetPreviewProps) {

    const { reloadDodPresets, reloadDodUniversalSavedSelections, user } = useApp()
    const { presets, setPreset, deletePreset, runConfig, setRunConfig } = useDodWizard();
    const [nonEmptyGroups, setNonEmptyNodes] = useState<DodSelectionPreviewNode[]>([]);
    const [count, setCount] = useState<number>(0);
    const isEmpty = Boolean(value && nonEmptyGroups?.length === 0);
    const { dodPresetsMap } = useDodService();
    const {dodUniversalSelectionsMap} = useUniversalSelection();

    const previewTitle = useMemo<string | JSX.Element>(() => {
        if (presets?.fact?.displayName) {
            return (
                <div className={`${baseClassName}__selection`}>
                    {`${count} selected using`}{' '}
                    <span
                        className={`${baseClassName}__preset-name`}
                        data-title={presets.fact.displayName}
                    >
                        {presets.fact.displayName}
                    </span>
                </div>
            );
        }
        return count > 0 ? `${count} Selected` : '';
    }, [presets, count]);

    useEffect(() => {

        const nonEmptyNodes: DodSelectionPreviewNode[] = [{
            label: 'Core Facts',
            key: 'core',
            values: value.map(factSetToCoreDisplayNames).flat(),
        }, {
            label: 'Share Facts',
            key: 'share',
            values: value.map(factSetToSharedDisplayNames).flat()
        }].filter(v => Boolean(v.values.length))
        setCount(nonEmptyNodes.reduce((total, { values }) => total + values!.length, 0));
        setNonEmptyNodes(nonEmptyNodes);
    }, [value]);

    async function handleDeleteGroup(group: DodSelectionPreviewNode) {
        let updatedValue: DodFactSet[] = value ?? [];
        const sortByFact = runConfig?.conditions?.products?.sortByFact;
        const groupValuesArray = Array.isArray(group?.values) ? group?.values : [];
        const factsInConditions = runConfig?.conditions?.facts.map(fact => fact.fact);
        
        // Check if any group value matches the sortByFact
        const sortByFactMatchesGroupValue = Boolean(sortByFact && groupValuesArray.includes(sortByFact));
        
        // Find intersection of group values being deleted and facts already in conditions
        const conflictingFacts = groupValuesArray.filter((val) => factsInConditions?.includes(val));

        // Priority 1: if sortByFact is being removed, clear the entire conditions object
        if (sortByFactMatchesGroupValue && sortByFact) {
            const affectedFacts: string[] = [sortByFact, ...conflictingFacts.filter((fact) => fact !== sortByFact)];
            if (await confirmConditionReset(affectedFacts)) {
                updatedValue = updatedValue.map((factVal) => deleteFactGroup(group?.key, factVal)).filter((factVal) => factHasOneAttribute(factVal));
                onChange?.({name, value: updatedValue});
                setRunConfig((prevRunConfig) => ({
                    ...prevRunConfig,
                    conditions: {
                        ...prevRunConfig.conditions,
                        facts: [],
                        products: { mode: 'All' },
                        name: '',
                    },
                }));
                if (presets.fact) {
                    deletePreset('fact');
                }
            }
        } else if (conflictingFacts.length > 0) {
            // Priority 2: if only condition facts are affected, remove just those facts and preserve custom names
            if (await confirmConditionReset(conflictingFacts)) {
                updatedValue = updatedValue.map((factVal) => deleteFactGroup(group?.key, factVal)).filter((factVal) => factHasOneAttribute(factVal));
                onChange?.({
                    name,
                    value: updatedValue,
                });
                setRunConfig((prevRunConfig) => ({
                    ...prevRunConfig,
                    conditions: (() => {
                        const nextConditions = {
                            ...prevRunConfig.conditions,
                            facts: prevRunConfig.conditions.facts.filter((fact) => !conflictingFacts.includes(fact.fact)),
                        };
                        const nextRunConfig = {
                            ...prevRunConfig,
                            conditions: nextConditions,
                        };

                        return {
                            ...nextConditions,
                            name: getConditionNamePreservingCustom(prevRunConfig, nextRunConfig),
                        };
                    })(),
                }));
                if (presets.fact) {
                    deletePreset('fact');
                }
            }
        } else {
            updatedValue = updatedValue.map((factVal) => deleteFactGroup(group?.key, factVal)).filter((factVal) => factHasOneAttribute(factVal));
            onChange?.({
                name,
                value: updatedValue,
            });
            if (presets.fact && updatedValue !== value) {
                deletePreset('fact');
            }
        }
    }

    async function handleDeleteGroupValue(mappedValue: string, group: DodSelectionPreviewNode) {
        let updatedValue: DodFactSet[] = value ?? [];
        const sortByFact = runConfig?.conditions?.products?.sortByFact;
        
        // Check if mappedValue matches the sortByFact
        if (sortByFact === mappedValue) {
            // Delete entire conditions if sorting fact is being removed
            if (await confirmConditionReset([mappedValue])) {
                updatedValue = updatedValue.map((factVal) => deleteFactValue(mappedValue, factVal)).filter((factVal) => factHasOneAttribute(factVal));
                onChange?.({name, value: updatedValue});
                setRunConfig((prevRunConfig) => ({
                    ...prevRunConfig,
                    conditions: {
                        ...prevRunConfig.conditions,
                        facts: [],
                        products: { mode: 'All' },
                        name: '',
                    },
                }));
                if (presets.fact) {
                    deletePreset('fact');
                }
            }
            return;
        }
        
        const factsInConditions = runConfig?.conditions?.facts.map(fact => fact.fact);
        
        if (factsInConditions?.includes(mappedValue)) {
            if (await confirmConditionReset([mappedValue])) {
                updatedValue = updatedValue.map((factVal) => deleteFactValue(mappedValue, factVal)).filter((factVal) => factHasOneAttribute(factVal));
                onChange?.({
                    name,
                    value: updatedValue,
                });
                setRunConfig((prevRunConfig) => ({
                    ...prevRunConfig,
                    conditions: (() => {
                        const nextConditions = {
                            ...prevRunConfig.conditions,
                            facts: prevRunConfig.conditions.facts.filter((fact) => fact.fact !== mappedValue),
                        };
                        const nextRunConfig = {
                            ...prevRunConfig,
                            conditions: nextConditions,
                        };

                        return {
                            ...nextConditions,
                            name: getConditionNamePreservingCustom(prevRunConfig, nextRunConfig),
                        };
                    })(),
                }));
            }
        } else {
            updatedValue = updatedValue.map((factVal) => deleteFactValue(mappedValue, factVal)).filter((factVal) => factHasOneAttribute(factVal));
            onChange?.({
                name,
                value: updatedValue,
            });
        }
        
        // remove fact preset
        if (presets.fact) {
            deletePreset('fact');
        }
    }

    function handleDeleteSummedSelection(
        group: DodSelectionPreviewNode,
        index: number
    ) {

    }

    function handleDeleteSummedSelectionValue(group: DodSelectionPreviewNode, index: number, value: string) {

    }

    function handleEditSummedSelection(group: DodSelectionPreviewNode, index: number) {

    }

    async function handleSaveSelections() {
        const preset = await openCreateDodPresetModal({
            description: savedSelectionDescription,
            currentPreset: presets['fact'],
            preset: {
                type: "fact",
                values: value
            },
            dodPresetsMap: dodPresetsMap["fact"],
            dodUniversalSelectionsMap: dodUniversalSelectionsMap["fact"],
            userEmail: user?.email,
            isDodSuperUser: user?.metadata?.isDodSuperUser || false,
            reloadDodUniversalSavedSelections,
            setPreset
        });

        if (preset) {
            setPreset('fact', {
                ...preset,
                // @ts-ignore - this needs to be here to remove values and produce a valid preset summary
                values: undefined
            })
            reloadDodPresets();
        }
    }

    // Function to generate the text content
    const generateSelectionText = () => {
        return nonEmptyGroups
            .map((group) => {
                // Group label
                const groupLabel = group.label;
                const includeAll = group.values === 'all';
                // Values under the group
                const valuesText = includeAll ? 'Includes all' : group.values?.map((value) => `  - ${value}`) // Indent values for readability
                    .join('\n');
                return `${groupLabel}:\n${valuesText}`;
            })
            .join('\n\n'); // Separate groups by a blank line
    };
    // Function to download the text as a file
    const downloadSelections = () => {
        const content = generateSelectionText();
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = DOMPurify.sanitize(url);
        a.download = `fact-selections.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };
    return (<ErrorBoundary fallback={<></>} onError={handleError}>
        <DodPanel className={classnames(baseClassName, className)}
            title={previewTitle}
            actions={[
                {
                    key: 'download',
                    iconType: 'download',
                    disabled: !count,
                    disabledTip: 'No selections to download',
                    tip: `Download all fact selections.`,
                    onClick: downloadSelections,
                },
                {
                    key: 'save',
                    disabled: !count,
                    iconType: 'save',
                    disabledTip: 'You have to select at least one value before you can save you selections.',
                    tip: 'Save your current selections to use in future runs.',
                    onClick: handleSaveSelections,
                }]}
            enableFilter={false}>
            <div className={`${baseClassName}__tree`}>
                {isEmpty && <div className={`${baseClassName}__empty-state`}>{emptyStateContent}</div>}
                {nonEmptyGroups?.map((group) => (<DodFilterPreviewGroup
                    key={group.key}
                    group={group}
                    onDelete={handleDeleteGroup}
                    onDeleteValue={handleDeleteGroupValue}
                    onDeleteSummedSelection={handleDeleteSummedSelection}
                    onDeleteSummedSelectionValue={handleDeleteSummedSelectionValue}
                    onEditSummedSelection={handleEditSummedSelection}
                    skipSorting={true}
                />
                ))}
            </div>
        </DodPanel>
    </ErrorBoundary>);
}

function handleError(err) {
    console.error(err);
}

function deleteFactValue(value: string = '', factSet: DodFactSet): DodFactSet {
    // Clone first so delete logic returns a fresh object and React sees the update immediately.
    const updatedFactSet: DodFactSet = {
        ...factSet,
    };

    const {
        category,
        categoryYearAgo,
        categoryTwoYearAgo,
        categoryThreeYearAgo,
        categoryFourYearAgo,
        categorySharePointChangeYearAgo,
        categorySharePointChangeTwoYearAgo,
        categorySharePointChangeThreeYearAgo,
        categorySharePointChangeFourYearAgo,
        brand,
        brandYearAgo,
        brandTwoYearAgo,
        brandThreeYearAgo,
        brandFourYearAgo,
        brandSharePointChangeYearAgo,
        brandSharePointChangeTwoYearAgo,
        brandSharePointChangeThreeYearAgo,
        brandSharePointChangeFourYearAgo,
        productSelections,
        productSelectionsYearAgo,
        productSelectionsTwoYearAgo,
        productSelectionsThreeYearAgo,
        productSelectionsFourYearAgo,
        productSelectionsSharePointChangeYearAgo,
        productSelectionsSharePointChangeTwoYearAgo,
        productSelectionsSharePointChangeThreeYearAgo,
        productSelectionsSharePointChangeFourYearAgo,
        fact: currentPeriod,
        yearAgo,
        changeVsYearAgo,
        percentChangeVsYearAgo,
        twoYearAgo,
        threeYearAgo,
        fourYearAgo,
        display,
        subCategory,
        subCategoryYearAgo,
        subCategoryTwoYearAgo,
        subCategoryThreeYearAgo,
        subCategoryFourYearAgo,
        subCategorySharePointChangeYearAgo,
        subCategorySharePointChangeTwoYearAgo,
        subCategorySharePointChangeThreeYearAgo,
        subCategorySharePointChangeFourYearAgo,
        changeVsFourYearAgo,
        changeVsThreeYearAgo,
        changeVsTwoYearAgo,
        percentChangeVsFourYearAgo,
        percentChangeVsThreeYearAgo,
        percentChangeVsTwoYearAgo
    } = updatedFactSet;

    if ((value.includes(DodFactSuffixes.perChangeYearAgo) && percentChangeVsYearAgo) && value.includes(display)) {
        updatedFactSet.percentChangeVsYearAgo = false;
    } else if (value.includes(DodFactSuffixes.changeYearAgo) && changeVsYearAgo && value.includes(display)) {
        updatedFactSet.changeVsYearAgo = false;
    } else if (value.includes(DodFactSuffixes.yearAgo) && yearAgo && value.includes(display)) {
        updatedFactSet.yearAgo = false;
    } else if ((value.includes(DodFactSuffixes.perChangeTwoYearAgo) && percentChangeVsTwoYearAgo) && value.includes(display)) {
        updatedFactSet.percentChangeVsTwoYearAgo = false;
    } else if (value.includes(DodFactSuffixes.changeTwoYearAgo) && changeVsTwoYearAgo && value.includes(display)) {
        updatedFactSet.changeVsTwoYearAgo = false;
    } else if (value.includes(DodFactSuffixes.twoYearAgo) && twoYearAgo && value.includes(display)) {
        updatedFactSet.twoYearAgo = false;
    } else if ((value.includes(DodFactSuffixes.perChangeThreeYearAgo) && percentChangeVsThreeYearAgo) && value.includes(display)) {
        updatedFactSet.percentChangeVsThreeYearAgo = false;
    } else if (value.includes(DodFactSuffixes.changeThreeYearAgo) && changeVsThreeYearAgo && value.includes(display)) {
        updatedFactSet.changeVsThreeYearAgo = false;
    } else if (value.includes(DodFactSuffixes.threeYearAgo) && threeYearAgo && value.includes(display)) {
        updatedFactSet.threeYearAgo = false;
    } else if ((value.includes(DodFactSuffixes.perChangeFourYearAgo) && percentChangeVsFourYearAgo) && value.includes(display)) {
        updatedFactSet.percentChangeVsFourYearAgo = false;
    } else if (value.includes(DodFactSuffixes.changeFourYearAgo) && changeVsFourYearAgo && value.includes(display)) {
        updatedFactSet.changeVsFourYearAgo = false;
    } else if (value.includes(DodFactSuffixes.fourYearAgo) && fourYearAgo && value.includes(display)) {
        updatedFactSet.fourYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareByCategoryFourYearAgo) && categoryFourYearAgo && value.includes(display)) {
        updatedFactSet.categoryFourYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareByCategoryThreeYearAgo) && categoryThreeYearAgo && value.includes(display)) {
        updatedFactSet.categoryThreeYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareByCategoryTwoYearAgo) && categoryTwoYearAgo && value.includes(display)) {
        updatedFactSet.categoryTwoYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareByCategoryYearAgo) && categoryYearAgo && value.includes(display)) {
        updatedFactSet.categoryYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareByCategory) && category && value.includes(display)) {
        updatedFactSet.category = false;
    } else if (value.includes(DodFactSuffixes.shareByCategorySharePointChangeYearAgo) && categorySharePointChangeFourYearAgo && value.includes(display)) {
        updatedFactSet.categorySharePointChangeFourYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareByCategorySharePointChangeThreeYearAgo) && categorySharePointChangeThreeYearAgo && value.includes(display)) {
        updatedFactSet.categorySharePointChangeThreeYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareByCategorySharePointChangeTwoYearAgo) && categorySharePointChangeTwoYearAgo && value.includes(display)) {
        updatedFactSet.categorySharePointChangeTwoYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareByCategorySharePointChangeYearAgo) && categorySharePointChangeYearAgo && value.includes(display)) {
        updatedFactSet.categorySharePointChangeYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareByBrandYearAgo) && brandYearAgo && value.includes(display)) {
        updatedFactSet.brandYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareByBrandTwoYearAgo) && brandTwoYearAgo && value.includes(display)) {
        updatedFactSet.brandTwoYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareByBrandThreeYearAgo) && brandThreeYearAgo && value.includes(display)) {
        updatedFactSet.brandThreeYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareByBrandFourYearAgo) && brandFourYearAgo && value.includes(display)) {
        updatedFactSet.brandFourYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareByBrand) && brand && value.includes(display)) {
        updatedFactSet.brand = false;
    } else if (value.includes(DodFactSuffixes.shareByBrandSharePointChangeYearAgo) && brandSharePointChangeYearAgo && value.includes(display)) {
        updatedFactSet.brandSharePointChangeYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareByBrandSharePointChangeTwoYearAgo) && brandSharePointChangeTwoYearAgo && value.includes(display)) {
        updatedFactSet.brandSharePointChangeTwoYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareByBrandSharePointChangeThreeYearAgo) && brandSharePointChangeThreeYearAgo && value.includes(display)) {
        updatedFactSet.brandSharePointChangeThreeYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareByBrandSharePointChangeFourYearAgo) && brandSharePointChangeFourYearAgo && value.includes(display)) {
        updatedFactSet.brandSharePointChangeFourYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareByProductSelectionsFourYearAgo) && productSelectionsFourYearAgo && value.includes(display)) {
        updatedFactSet.productSelectionsFourYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareByProductSelectionsThreeYearAgo) && productSelectionsThreeYearAgo && value.includes(display)) {
        updatedFactSet.productSelectionsThreeYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareByProductSelectionsTwoYearAgo) && productSelectionsTwoYearAgo && value.includes(display)) {
        updatedFactSet.productSelectionsTwoYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareByProductSelectionsYearAgo) && productSelectionsYearAgo && value.includes(display)) {
        updatedFactSet.productSelectionsYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareByProductSelections) && productSelections && value.includes(display)) {
        updatedFactSet.productSelections = false;
    } else if (value.includes(DodFactSuffixes.shareByProductSelectionsSharePointChangeFourYearAgo) && productSelectionsSharePointChangeFourYearAgo && value.includes(display)) {
        updatedFactSet.productSelectionsSharePointChangeFourYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareByProductSelectionsSharePointChangeThreeYearAgo) && productSelectionsSharePointChangeThreeYearAgo && value.includes(display)) {
        updatedFactSet.productSelectionsSharePointChangeThreeYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareByProductSelectionsSharePointChangeTwoYearAgo) && productSelectionsSharePointChangeTwoYearAgo && value.includes(display)) {
        updatedFactSet.productSelectionsSharePointChangeTwoYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareByProductSelectionsSharePointChangeYearAgo) && productSelectionsSharePointChangeYearAgo && value.includes(display)) {
        updatedFactSet.productSelectionsSharePointChangeYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareBySubcategory) && subCategory && value.includes(display)) {
        updatedFactSet.subCategory = false;
    } else if (value.includes(DodFactSuffixes.shareBySubcategoryYearsAgo) && subCategoryYearAgo && value.includes(display)) {
        updatedFactSet.subCategoryYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareBySubcategoryTwoYearsAgo) && subCategoryTwoYearAgo && value.includes(display)) {
        updatedFactSet.subCategoryTwoYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareBySubcategoryThreeYearsAgo) && subCategoryThreeYearAgo && value.includes(display)) {
        updatedFactSet.subCategoryThreeYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareBySubcategoryFourYearsAgo) && subCategoryFourYearAgo && value.includes(display)) {
        updatedFactSet.subCategoryFourYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareBySubcategorySharePointChangeYearAgo) && subCategorySharePointChangeYearAgo && value.includes(display)) {
        updatedFactSet.subCategorySharePointChangeYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareBySubcategorySharePointChangeTwoYearAgo) && subCategorySharePointChangeTwoYearAgo && value.includes(display)) {
        updatedFactSet.subCategorySharePointChangeTwoYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareBySubcategorySharePointChangeThreeYearAgo) && subCategorySharePointChangeThreeYearAgo && value.includes(display)) {
        updatedFactSet.subCategorySharePointChangeThreeYearAgo = false;
    } else if (value.includes(DodFactSuffixes.shareBySubcategorySharePointChangeFourYearAgo) && subCategorySharePointChangeFourYearAgo && value.includes(display)) {
        updatedFactSet.subCategorySharePointChangeFourYearAgo = false;
    } else if (currentPeriod && value.includes(display)) {
        updatedFactSet.fact = false;
    }
    return updatedFactSet;
}

function deleteFactGroup(type: string, factSet: DodFactSet): DodFactSet {
    // Clone first so group-level deletions stay immutable and rerender reliably.
    const updatedFactSet: DodFactSet = {
        ...factSet,
    };

    if (type === 'core') {
        updatedFactSet.fact = false;
        updatedFactSet.yearAgo = false;
        updatedFactSet.changeVsYearAgo = false;
        updatedFactSet.percentChangeVsYearAgo = false;
        updatedFactSet.twoYearAgo = false;
        updatedFactSet.changeVsTwoYearAgo = false;
        updatedFactSet.percentChangeVsTwoYearAgo = false;
        updatedFactSet.threeYearAgo = false;
        updatedFactSet.changeVsThreeYearAgo = false;
        updatedFactSet.percentChangeVsThreeYearAgo = false;
        updatedFactSet.fourYearAgo = false;
        updatedFactSet.changeVsFourYearAgo = false;
        updatedFactSet.percentChangeVsFourYearAgo = false;
    } else if (type === 'share') {
        updatedFactSet.category = false;
        updatedFactSet.categoryYearAgo = false;
        updatedFactSet.categoryTwoYearAgo = false;
        updatedFactSet.categoryThreeYearAgo = false;
        updatedFactSet.categoryFourYearAgo = false;
        updatedFactSet.categorySharePointChangeYearAgo = false;
        updatedFactSet.categorySharePointChangeTwoYearAgo = false;
        updatedFactSet.categorySharePointChangeThreeYearAgo = false;
        updatedFactSet.categorySharePointChangeFourYearAgo = false;
        updatedFactSet.brand = false;
        updatedFactSet.brandYearAgo = false;
        updatedFactSet.brandTwoYearAgo = false;
        updatedFactSet.brandThreeYearAgo = false;
        updatedFactSet.brandFourYearAgo = false;
        updatedFactSet.brandSharePointChangeYearAgo = false;
        updatedFactSet.brandSharePointChangeTwoYearAgo = false;
        updatedFactSet.brandSharePointChangeThreeYearAgo = false;
        updatedFactSet.brandSharePointChangeFourYearAgo = false;
        updatedFactSet.productSelections = false;
        updatedFactSet.productSelectionsYearAgo = false;
        updatedFactSet.productSelectionsTwoYearAgo = false;
        updatedFactSet.productSelectionsThreeYearAgo = false;
        updatedFactSet.productSelectionsFourYearAgo = false;
        updatedFactSet.productSelectionsSharePointChangeYearAgo = false;
        updatedFactSet.productSelectionsSharePointChangeTwoYearAgo = false;
        updatedFactSet.productSelectionsSharePointChangeThreeYearAgo = false;
        updatedFactSet.productSelectionsSharePointChangeFourYearAgo = false;
        updatedFactSet.subCategory = false;
        updatedFactSet.subCategoryYearAgo = false;
        updatedFactSet.subCategoryTwoYearAgo = false;
        updatedFactSet.subCategoryThreeYearAgo = false;
        updatedFactSet.subCategoryFourYearAgo = false;
        updatedFactSet.subCategorySharePointChangeYearAgo = false;
        updatedFactSet.subCategorySharePointChangeTwoYearAgo = false;
        updatedFactSet.subCategorySharePointChangeThreeYearAgo = false;
        updatedFactSet.subCategorySharePointChangeFourYearAgo = false;
    }

    return updatedFactSet;
}

function factHasOneAttribute(factSet: DodFactSet): Boolean {
    return Boolean(factSet.fact ||
        factSet.yearAgo ||
        factSet.changeVsYearAgo ||
        factSet.percentChangeVsYearAgo ||
        factSet.twoYearAgo ||
        factSet.changeVsTwoYearAgo ||
        factSet.percentChangeVsTwoYearAgo ||
        factSet.threeYearAgo ||
        factSet.changeVsThreeYearAgo ||
        factSet.percentChangeVsThreeYearAgo ||
        factSet.fourYearAgo ||
        factSet.changeVsFourYearAgo ||
        factSet.percentChangeVsFourYearAgo ||
        factSet.category ||
        factSet.categoryYearAgo ||
        factSet.categoryTwoYearAgo ||
        factSet.categoryThreeYearAgo ||
        factSet.categoryFourYearAgo ||
        factSet.categorySharePointChangeYearAgo ||
        factSet.categorySharePointChangeTwoYearAgo ||
        factSet.categorySharePointChangeThreeYearAgo ||
        factSet.categorySharePointChangeFourYearAgo ||
        factSet.brand ||
        factSet.brandYearAgo ||
        factSet.brandTwoYearAgo ||
        factSet.brandThreeYearAgo ||
        factSet.brandFourYearAgo ||
        factSet.brandSharePointChangeYearAgo ||
        factSet.brandSharePointChangeTwoYearAgo ||
        factSet.brandSharePointChangeThreeYearAgo ||
        factSet.brandSharePointChangeFourYearAgo ||
        factSet.productSelections ||
        factSet.productSelectionsYearAgo ||
        factSet.productSelectionsTwoYearAgo ||
        factSet.productSelectionsThreeYearAgo ||
        factSet.productSelectionsFourYearAgo ||
        factSet.productSelectionsSharePointChangeYearAgo ||
        factSet.productSelectionsSharePointChangeTwoYearAgo ||
        factSet.productSelectionsSharePointChangeThreeYearAgo ||
        factSet.productSelectionsSharePointChangeFourYearAgo ||
        factSet.subCategory ||
        factSet.subCategoryYearAgo ||
        factSet.subCategoryTwoYearAgo ||
        factSet.subCategoryThreeYearAgo ||
        factSet.subCategoryFourYearAgo ||
        factSet.subCategorySharePointChangeYearAgo ||
        factSet.subCategorySharePointChangeTwoYearAgo ||
        factSet.subCategorySharePointChangeThreeYearAgo ||
        factSet.subCategorySharePointChangeFourYearAgo);
}

export async function confirmConditionReset(affectedFacts: string[] = []): Promise<boolean> {
    return confirm({
        title: 'Warning',
        content: (
            <>
                <p>
                    You are about to remove facts that are currently used in your conditions. This action will delete all the conditions associated with these facts.
                </p>
                <p style={{ fontWeight: 'bold', marginTop: '12px' }}>
                    The following facts will have their conditions removed:
                </p>
                {affectedFacts.length > 0 && (
                    <div style={{ marginLeft: '16px', marginTop: '8px', marginBottom: '12px' }}>
                        {affectedFacts.map(fact => (
                            <p key={fact} style={{ margin: '4px 0' }}>
                                • {fact}
                            </p>
                        ))}
                    </div>
                )}
                <p style={{ marginTop: '12px' }}>
                    Click <strong>"Continue"</strong> to proceed or <strong>"Cancel"</strong> to keep the facts and their conditions intact.
                </p>
            </>
        ),
        yesLabel: 'Continue',
        noLabel: 'Cancel',
    });
}

export default DodFactSetPreview;
